import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';

const LANG_LABELS = { curl: 'cURL', python: 'Python', js: 'JavaScript' };

const SYNTAX_COLORS = {
  string: 'text-emerald-400',
  key: 'text-sky-300',
  number: 'text-amber-300',
  boolean: 'text-purple-400',
  null: 'text-slate-500',
  comment: 'text-slate-500 italic',
  url: 'text-sky-400',
  method: 'text-pink-400',
  flag: 'text-amber-200',
};

// Very lightweight token coloriser for code blocks
function colorise(code) {
  // We return pre-styled spans line-by-line
  return code.split('\n').map((line, i) => {
    // Comment lines (# ...)
    if (/^\s*#/.test(line)) {
      return <span key={i} className={SYNTAX_COLORS.comment}>{line}{'\n'}</span>;
    }
    // JSON / response colorisation
    const parts = [];
    let remaining = line;
    let key = 0;

    // Tokenise: "key": value patterns
    const jsonPattern = /("[\w_-]+")\s*:\s*(".*?"|true|false|null|\d+\.?\d*)/g;
    let last = 0;
    let match;
    jsonPattern.lastIndex = 0;

    while ((match = jsonPattern.exec(remaining)) !== null) {
      if (match.index > last) parts.push(<span key={key++}>{remaining.slice(last, match.index)}</span>);
      parts.push(<span key={key++} className={SYNTAX_COLORS.key}>{match[1]}</span>);
      parts.push(<span key={key++}>: </span>);
      const val = match[2];
      if (val.startsWith('"')) parts.push(<span key={key++} className={SYNTAX_COLORS.string}>{val}</span>);
      else if (val === 'true' || val === 'false') parts.push(<span key={key++} className={SYNTAX_COLORS.boolean}>{val}</span>);
      else if (val === 'null') parts.push(<span key={key++} className={SYNTAX_COLORS.null}>{val}</span>);
      else parts.push(<span key={key++} className={SYNTAX_COLORS.number}>{val}</span>);
      last = match.index + match[0].length;
    }

    if (last < remaining.length) parts.push(<span key={key++}>{remaining.slice(last)}</span>);

    return (
      <span key={i}>
        {parts.length > 0 ? parts : remaining}
        {'\n'}
      </span>
    );
  });
}

export function CodePanel({ examples, defaultLang = 'curl', sharedLang, onLangChange }) {
  const activeLang = sharedLang || defaultLang;
  const code = examples[activeLang] || examples.curl || '';

  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const availableLangs = Object.keys(examples).filter(l => examples[l]);

  return (
    <div className="flex flex-col h-full bg-slate-950 rounded-xl overflow-hidden border border-slate-800">
      {/* Lang tabs */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800 bg-slate-900 flex-shrink-0 gap-2">
        <div className="flex gap-1 overflow-x-auto scrollbar-none flex-1">
          {availableLangs.map(lang => (
            <button
              key={lang}
              onClick={() => onLangChange && onLangChange(lang)}
              className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${
                activeLang === lang
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              {LANG_LABELS[lang] || lang}
            </button>
          ))}
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {/* Code */}
      <pre className="flex-1 overflow-auto p-4 text-xs leading-relaxed font-mono text-slate-300 whitespace-pre">
        {colorise(code)}
      </pre>
    </div>
  );
}

export function ResponseBlock({ response }) {
  const [copied, setCopied] = useState(false);
  if (!response) return null;

  return (
    <div className="bg-slate-950 rounded-xl border border-slate-800 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-900 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-400" />
          <span className="text-xs font-semibold text-slate-400">200 Response</span>
        </div>
        <button
          onClick={() => { navigator.clipboard.writeText(response); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="p-4 text-xs leading-relaxed font-mono text-slate-300 whitespace-pre overflow-auto">
        {colorise(response)}
      </pre>
    </div>
  );
}
